// Step 3: every YouTrack project under its customer. A customer chooses its
// company (an existing one, or a new one with its domain); a project chooses
// whether it comes at all, and into which project of that company. Every
// edit is saved as it is made; nothing is created until the import starts.
// A BookStack batch lists shelves instead, each choosing a Context Hub space.
import { type KeyboardEvent, useEffect, useMemo, useState } from "react"
import { Badge, Button, ErrorNote } from "../../ui"
import { api } from "../../../lib/api"
import {
  type Batch,
  type BatchItem,
  type CompanyTarget,
  type ProjectTarget,
  type SpaceTarget,
  type TargetCompany,
  UNASSIGNED,
  groupByCustomer,
  isOld,
  isValidDomain,
  isValidKey,
  keyFor,
} from "../../../lib/batchMigrations"
import { formatCount } from "../../../lib/migrations"
import { Note, StepLayout } from "../parts"

const NEW = "__new"
const inputClass =
  "rounded-md border border-border bg-surface px-2 py-1 text-sm outline-none placeholder:text-faint focus:border-interactive focus:ring-2 focus:ring-interactive/30"

/** A text box that saves on blur or Enter, and only when the text changed:
 *  saving on every keystroke would re-plan the batch for each letter. */
function SaveOnBlur({
  value,
  onSave,
  className = "",
  placeholder,
  ariaLabel,
  list,
  invalid,
}: {
  value: string
  onSave: (v: string) => void
  className?: string
  placeholder?: string
  ariaLabel: string
  list?: string
  invalid?: (v: string) => boolean
}) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  const commit = () => {
    const v = draft.trim()
    if (v === value || !v || invalid?.(v)) return setDraft(value)
    onSave(v)
  }
  const bad = Boolean(draft.trim() && invalid?.(draft.trim()))
  return (
    <input
      aria-label={ariaLabel}
      aria-invalid={bad || undefined}
      className={`${inputClass} ${bad ? "border-danger" : ""} ${className}`}
      value={draft}
      list={list}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur()
        if (e.key === "Escape") setDraft(value)
      }}
    />
  )
}

export function SelectStep({
  batch,
  companies,
  onChange,
  onReupload,
  onCancel,
}: {
  batch: Batch
  companies: TargetCompany[]
  onChange: (b: Batch) => void
  onReupload: () => void
  onCancel: () => void
}) {
  const [hideOld, setHideOld] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const groups = useMemo(() => groupByCustomer(batch.items), [batch.items])
  const customers = groups.map((g) => g.customer).filter((c) => c !== UNASSIGNED)
  const selected = batch.items.filter((i) => i.selected)
  const base = `/platform/migrations/batches/${batch.id}`
  const bookstack = batch.source === "bookstack"
  const unit = (n: number) => (bookstack ? (n === 1 ? "shelf" : "shelves") : n === 1 ? "project" : "projects")

  const save = async (call: () => Promise<{ batch: Batch }>) => {
    setBusy(true)
    setError(null)
    try {
      onChange((await call()).batch)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }
  const patchItems = (items: Array<Record<string, unknown>>) => save(() => api.patch(`${base}/items`, { items }))
  const patchCustomer = (customer: string, company: CompanyTarget) => save(() => api.patch(`${base}/customers`, { customer, company }))

  const preview = () => save(() => api.post(`${base}/preview`))
  const missing = selected.filter((i) => !i.companyTarget || !i.projectTarget)

  return (
    <StepLayout
      title="Choose what comes across, and where"
      intro={`${formatCount(selected.length)} of ${formatCount(batch.items.length)} ${unit(batch.items.length)} ticked, across ${formatCount(customers.length)} customers.`}
      guide={bookstack ? {
        points: [
          "Each customer lands in one company: an existing one, or a new one created when the import starts.",
          "Each shelf becomes one Context Hub space holding all of its books, or goes into a BookStack space the company already has.",
          "A shelf named like an existing company was matched and ticked; the others are proposed as new companies and left unticked.",
          "Each space keeps the token, so it can sync again from its own page.",
        ],
        tip: "A shelf already imported into a space of that company goes back into it, bringing only what changed.",
      } : {
        points: [
          "Each customer lands in one company: an existing one, or a new one created when the import starts.",
          "Each project becomes a new project in that company, keyed like YouTrack, or is added to one the company already has.",
          "Statuses, types, priorities, fields and sprints are created as they are in YouTrack. Nothing is mapped by hand.",
          "People are linked by email; anyone unknown is added disabled, and nobody is emailed.",
        ],
        tip: "A project already imported before goes back into the project that holds it, bringing only what changed.",
      }}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel batch
          </Button>
          <Button type="button" variant="ghost" onClick={onReupload}>
            {bookstack ? "Download or upload the mapping" : "Upload a different file"}
          </Button>
          <Button type="button" className="ml-auto" disabled={busy || !selected.length || missing.length > 0} onClick={preview}>
            Preview {formatCount(selected.length)} {unit(selected.length)}
          </Button>
        </>
      }
    >
      {!bookstack && <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={hideOld} onChange={(e) => setHideOld(e.target.checked)} />
          Hide projects named "OLD - …"
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={batch.includeRestricted}
            disabled={busy}
            onChange={(e) => save(() => api.patch(base, { includeRestricted: e.target.checked }))}
          />
          Include issues restricted to a group in YouTrack
        </label>
      </div>}
      <ErrorNote error={error} />
      {missing.length > 0 && (
        <Note tone="warning">
          {missing.map((i) => i.sourceProject.key).join(", ")} {missing.length === 1 ? "has" : "have"} no customer or {bookstack ? "space" : "project"} yet.
        </Note>
      )}
      <datalist id="batch-customers">
        {customers.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
      <div className="flex flex-col gap-4">
        {groups.map((g) => (
          <CustomerBlock
            key={g.customer}
            customer={g.customer}
            items={hideOld ? g.items.filter((i) => !isOld(i)) : g.items}
            companies={companies}
            bookstack={bookstack}
            disabled={busy}
            onCompany={(company) => patchCustomer(g.customer, company)}
            onItems={patchItems}
          />
        ))}
      </div>
    </StepLayout>
  )
}

function CustomerBlock({
  customer,
  items,
  companies,
  bookstack,
  disabled,
  onCompany,
  onItems,
}: {
  customer: string
  items: BatchItem[]
  companies: TargetCompany[]
  bookstack: boolean
  disabled: boolean
  onCompany: (company: CompanyTarget) => void
  onItems: (items: Array<Record<string, unknown>>) => void
}) {
  if (!items.length) return null
  const unassigned = customer === UNASSIGNED
  // Every item of a customer shares its company; the first one says which.
  const target = items.find((i) => i.companyTarget)?.companyTarget ?? null
  const company = target && "companyId" in target ? companies.find((c) => c.id === target.companyId) : undefined
  const ticked = items.filter((i) => i.selected).length

  return (
    <section className="rounded-lg border border-border">
      <header className="flex flex-wrap items-center gap-3 border-b border-border bg-surface-muted px-3 py-2">
        <h3 className="text-sm font-semibold text-foreground">{customer}</h3>
        <span className="text-xs text-muted">
          {ticked} of {items.length} ticked
        </span>
        {!unassigned && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted">→ company</span>
            <select
              aria-label={`Company for ${customer}`}
              className={inputClass}
              disabled={disabled}
              value={target ? ("companyId" in target ? target.companyId : NEW) : ""}
              onChange={(e) => {
                const v = e.target.value
                if (v === NEW) {
                  const domain = `${customer.toLowerCase().replace(/\([^)]*\)/g, "").replace(/[^a-z0-9]+/g, "")}.com`
                  onCompany({ new: { name: customer, domain } })
                } else if (v) onCompany({ companyId: v })
              }}
            >
              {!target && <option value="">Choose…</option>}
              <option value={NEW}>Create a new company</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.domain})
                </option>
              ))}
            </select>
            {target && "new" in target && (
              <>
                <SaveOnBlur
                  ariaLabel={`New company name for ${customer}`}
                  value={target.new.name}
                  onSave={(name) => onCompany({ new: { ...target.new, name } })}
                />
                <SaveOnBlur
                  ariaLabel={`Domain for ${customer}`}
                  value={target.new.domain}
                  invalid={(v) => !isValidDomain(v.toLowerCase())}
                  onSave={(domain) => onCompany({ new: { ...target.new, domain: domain.toLowerCase() } })}
                />
                <Badge tone="accent">new</Badge>
              </>
            )}
          </div>
        )}
        <div className="ml-auto flex gap-1">
          <Button
            type="button"
            size="sm"
            variant="subtle"
            disabled={disabled || unassigned}
            onClick={() => onItems(items.filter((i) => !i.selected).map((i) => ({ id: i.id, selected: true })))}
          >
            All
          </Button>
          <Button
            type="button"
            size="sm"
            variant="subtle"
            disabled={disabled}
            onClick={() => onItems(items.filter((i) => i.selected).map((i) => ({ id: i.id, selected: false })))}
          >
            None
          </Button>
        </div>
      </header>
      <ul className="divide-y divide-border">
        {items.map((item) =>
          bookstack ? (
            <ShelfRow key={item.id} item={item} company={company} disabled={disabled} onItems={onItems} />
          ) : (
            <ProjectRow key={item.id} item={item} company={company} disabled={disabled} onItems={onItems} />
          ),
        )}
      </ul>
    </section>
  )
}

function ProjectRow({
  item,
  company,
  disabled,
  onItems,
}: {
  item: BatchItem
  company: TargetCompany | undefined
  disabled: boolean
  onItems: (items: Array<Record<string, unknown>>) => void
}) {
  const unassigned = item.customer === UNASSIGNED
  const target: ProjectTarget | null = item.projectTarget
  const set = (change: Record<string, unknown>) => onItems([{ id: item.id, ...change }])

  return (
    <li className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
      <input
        type="checkbox"
        aria-label={`Import ${item.sourceProject.key}`}
        checked={item.selected}
        disabled={disabled || unassigned}
        onChange={(e) => set({ selected: e.target.checked })}
      />
      <div className="min-w-48 flex-1">
        <p className="text-foreground">
          <span className="font-mono text-xs text-muted">{item.sourceProject.key}</span> {item.sourceProject.name}
        </p>
        <p className="text-xs text-faint">
          {formatCount(item.sourceProject.issueCount)} issues
          {item.boards.length > 0 &&
            ` · sprints from ${item.boards.map((b) => `${b.name} (${b.sprints}${b.shared ? ", shared" : ""})`).join(", ")}`}
        </p>
      </div>
      {unassigned ? (
        <SaveOnBlur
          ariaLabel={`Customer for ${item.sourceProject.key}`}
          value=""
          list="batch-customers"
          placeholder="Give it a customer…"
          onSave={(customer) => set({ customer })}
        />
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted">→</span>
          <select
            aria-label={`Target project for ${item.sourceProject.key}`}
            className={inputClass}
            disabled={disabled}
            value={target ? ("projectId" in target ? target.projectId : NEW) : ""}
            onChange={(e) => {
              const v = e.target.value
              if (v === NEW) {
                set({ project: { new: { name: item.sourceProject.name.slice(0, 100), keyPrefix: keyFor(item.sourceProject.key) } } })
              } else if (v) set({ project: { projectId: v } })
            }}
          >
            {!target && <option value="">Choose…</option>}
            <option value={NEW}>New project</option>
            {company?.projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.keyPrefix} · {p.name}
              </option>
            ))}
          </select>
          {target && "new" in target && (
            <>
              <SaveOnBlur
                ariaLabel={`Name for ${item.sourceProject.key}`}
                value={target.new.name}
                onSave={(name) => set({ project: { new: { ...target.new, name } } })}
              />
              <SaveOnBlur
                ariaLabel={`Key for ${item.sourceProject.key}`}
                className="w-28 font-mono"
                value={target.new.keyPrefix}
                invalid={(v) => !isValidKey(v.toUpperCase())}
                onSave={(keyPrefix) => set({ project: { new: { ...target.new, keyPrefix: keyPrefix.toUpperCase() } } })}
              />
            </>
          )}
          <label className="flex items-center gap-1 text-xs text-muted" title="Keep YouTrack's issue numbers, so CLLSA-176 stays 176">
            <input
              type="checkbox"
              checked={item.keepSourceNumbers}
              disabled={disabled}
              onChange={(e) => set({ keepSourceNumbers: e.target.checked })}
            />
            Keep numbers
          </label>
        </div>
      )}
    </li>
  )
}

/** A BookStack shelf: ticked or not, and the space it lands in. */
function ShelfRow({
  item,
  company,
  disabled,
  onItems,
}: {
  item: BatchItem
  company: TargetCompany | undefined
  disabled: boolean
  onItems: (items: Array<Record<string, unknown>>) => void
}) {
  const unassigned = item.customer === UNASSIGNED
  const target = item.projectTarget as unknown as SpaceTarget | null
  const books = item.boards as unknown as Array<{ name: string }>
  const set = (change: Record<string, unknown>) => onItems([{ id: item.id, ...change }])

  return (
    <li className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
      <input
        type="checkbox"
        aria-label={`Import ${item.sourceProject.key}`}
        checked={item.selected}
        disabled={disabled || unassigned}
        onChange={(e) => set({ selected: e.target.checked })}
      />
      <div className="min-w-48 flex-1">
        <p className="text-foreground">
          {item.sourceProject.name} <span className="font-mono text-xs text-muted">{item.sourceProject.key}</span>
        </p>
        <p className="text-xs text-faint">
          {formatCount(books.length)} book{books.length === 1 ? "" : "s"}
          {books.length > 0 && `: ${books.map((b) => b.name).join(", ")}`}
        </p>
      </div>
      {unassigned ? (
        <SaveOnBlur
          ariaLabel={`Customer for ${item.sourceProject.key}`}
          value=""
          list="batch-customers"
          placeholder="Give it a customer…"
          onSave={(customer) => set({ customer })}
        />
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted">→ space</span>
          <select
            aria-label={`Target space for ${item.sourceProject.key}`}
            className={inputClass}
            disabled={disabled}
            value={target ? ("spaceId" in target ? target.spaceId : NEW) : ""}
            onChange={(e) => {
              const v = e.target.value
              if (v === NEW) set({ project: { new: { name: item.sourceProject.name.slice(0, 200) } } })
              else if (v) set({ project: { spaceId: v } })
            }}
          >
            {!target && <option value="">Choose…</option>}
            <option value={NEW}>New space</option>
            {company?.spaces?.map((sp) => (
              <option key={sp.id} value={sp.id}>
                {sp.name}
              </option>
            ))}
          </select>
          {target && "new" in target && (
            <SaveOnBlur
              ariaLabel={`Space name for ${item.sourceProject.key}`}
              value={target.new.name}
              onSave={(name) => set({ project: { new: { name } } })}
            />
          )}
        </div>
      )}
    </li>
  )
}
